/**
 * MarkdownContent
 *
 * Chat-ui wrapper around the design-system MarkdownRenderer, so end-user chat
 * renders markdown with the same treatment as admin-ui's mission chat
 * (MarkdownText). It replaced `prose` classes that never took effect: chat-ui
 * is Tailwind v4 CSS-first and never loaded the typography plugin (Agion#2060).
 */

import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { MarkdownRenderer, type MarkdownRendererProps } from '@agion/ui'

// amber-700 rather than the design-system's amber-600: amber-600 on white is
// below AA contrast for body-size text.
export const MarkdownContent: React.FC<{ content: string }> = ({ content }) => (
  <MarkdownRenderer
    content={content}
    ReactMarkdown={ReactMarkdown as MarkdownRendererProps['ReactMarkdown']}
    remarkGfm={remarkGfm}
    compact
    className={[
      'max-w-none overflow-hidden break-words [overflow-wrap:anywhere]',
      '[&_a]:text-amber-700 dark:[&_a]:text-amber-400 [&_a]:underline [&_a]:font-medium',
      // Agents emit `---` and headings to structure long instructions; keep the rhythm.
      '[&_hr]:my-3 [&_h2]:mt-3 [&_h3]:mt-3 [&_h2:first-child]:mt-0 [&_h3:first-child]:mt-0',
      '[&_table]:my-2 [&_table]:text-xs [&_th]:normal-case [&_th]:tracking-normal',
      '[&_pre]:max-w-full [&_pre]:overflow-x-auto',
      // Agents cite sources as inline code; plain monospace keeps long answers
      // light. Code blocks (pre > code) keep the design-system treatment.
      '[&_:not(pre)>code]:bg-transparent [&_:not(pre)>code]:p-0 [&_:not(pre)>code]:text-[0.9em]',
      '[&_:not(pre)>code]:text-slate-600 dark:[&_:not(pre)>code]:text-slate-300',
    ].join(' ')}
  />
)
